import React from 'react';
import { X, TrendingUp, Wallet, Percent } from 'lucide-react';

const STORAGE_KEY = 'wayja_share_priced_trading_intro_dismissed';

export function hasDismissedSharePricedIntro(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissSharePricedIntroPersist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
}

interface SharePricedTradingIntroModalProps {
  onClose: () => void;
}

/**
 * One-time explainer for share-priced (fixed-price) markets: shares, live price, fee-on-profit.
 * Distinct from FirstTradeGateModal (phone verification after first trade).
 */
export function SharePricedTradingIntroModal({ onClose }: SharePricedTradingIntroModalProps) {
  const handleClose = () => {
    dismissSharePricedIntroPersist();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[45] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-gray-900 pr-8 mb-1">How share-priced markets work</h2>
        <p className="text-sm text-gray-500 mb-5">Quick overview before you trade</p>

        <ul className="space-y-4 text-sm text-gray-700">
          <li className="flex gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wayja-teal/10 text-wayja-dark-teal">
              <TrendingUp className="w-4 h-4" />
            </span>
            <span>
              <strong className="text-gray-900">Live price.</strong> You buy <em>shares</em> in an outcome at the
              current price. The price moves as others trade—shown as R per share and implied chance.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wayja-teal/10 text-wayja-dark-teal">
              <Wallet className="w-4 h-4" />
            </span>
            <span>
              <strong className="text-gray-900">Sell before resolution.</strong> If the market is still open, you
              can sell shares back to reduce your position. Proceeds are based on the same pricing engine.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wayja-teal/10 text-wayja-dark-teal">
              <Percent className="w-4 h-4" />
            </span>
            <span>
              <strong className="text-gray-900">Fee on profit only.</strong> Nothing extra is taken when you buy.
              If you win at resolution—or sell at a gain—a performance fee applies to your <em>profit</em> on that
              trade, not to your stake. The exact rate is set per market; you will see it in the trade summary.
            </span>
          </li>
        </ul>

        <button
          type="button"
          onClick={handleClose}
          className="mt-6 w-full px-6 py-3 bg-wayja-teal text-white rounded-xl font-medium hover:bg-opacity-90 transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
